/**
 * Report catalog (REPORTS-IA) — the one list of reports: sections, titles,
 * blurbs, icons. Shared by the Reports rail AND the top-bar breadcrumb
 * (Company › Reports › Revenue & Sales), so the name of a report lives in
 * exactly one place.
 */
import {
  PackageSearch, DollarSign,
  Megaphone, ShieldCheck, Calendar, Sparkles, TrendingUp, SlidersHorizontal, Lightbulb, Percent,
  ScanLine, CalendarClock, ClipboardList, ListChecks, Receipt, HardHat, Banknote,
  Users, RefreshCw, Wrench, Truck, ShoppingCart, Snowflake, ClipboardCheck, type LucideIcon,
} from "lucide-react";

export type ReportKey = "equipment-installs" | "seasonal-demand" | "classification-review" | "revenue" | "margin" | "dispatch" | "insights" | "agreements" | "serial-lookup" | "warranty-expiring" | "dispatch-list" | "invoice-list" | "po-list" | "vendor-list" | "ticket-list" | "tech-payroll" | "collections" | "customers" | "membership-renewals" | "tuneups-due" | "parts-day" | "lead-sources" | "refrigerant" | "stock-counts" | "ask";

export interface ReportCard {
  key: ReportKey | string;
  title: string;
  blurb: string;
  Icon: LucideIcon;
  live: boolean;
}

/** REPORTS-IA (2026-06-10 review): grouped into sections so the picker scales
 *  — Lists = row-level (the ESC List screens reborn), Analytics = aggregates,
 *  Lookups = find-one worklists, AI = the smart layer. Lists vs their
 *  aggregate twins (Dispatch list vs Dispatch volume; Invoice list vs
 *  Revenue) stay separate on purpose: rows answer "show me them",
 *  aggregates answer "how many / how much". */
export const SECTIONS: { label: string; reports: ReportCard[] }[] = [
  // REPORTS-HOME-02 (Seth 2026-09-02: "Ask Pegasus as the default landing
  // page of reports… the briefings on that same default page, but Ask
  // Pegasus should be the primary focus"): the AI group leads the rail and
  // Ask Pegasus is its first row — it is also the page /reports opens on.
  {
    label: "Ask Pegasus",
    reports: [
      { key: "ask", title: "Ask Pegasus", blurb: "Type a question in plain English; AI builds the report from your real data. The daily briefing sits on the same page.", Icon: Sparkles, live: true },
      { key: "insights", title: "Briefing", blurb: "AI-surfaced signals worth noticing — revenue swings, the R-454B ramp, owed visits.", Icon: Lightbulb, live: true },
      { key: "classification-review", title: "Model Classifications", blurb: "Review & confirm the AI's equipment-model labels. Your picks win.", Icon: SlidersHorizontal, live: true },
    ],
  },
  {
    label: "Lists",
    reports: [
      { key: "customers", title: "All Customers", blurb: "Every customer, filtered by anything — city, zip, plan status, has-email, balance — then email the whole filtered list. Save your own views, export CSV.", Icon: Users, live: true },
      { key: "dispatch-list", title: "Dispatch List", blurb: "Every dispatch as a flat, sortable list — any date range, saved views, CSV.", Icon: ClipboardList, live: true },
      { key: "invoice-list", title: "Invoice List", blurb: "Invoices over any period with search, department refine, a grand total and CSV.", Icon: Receipt, live: true },
      { key: "po-list", title: "Purchase Order List", blurb: "Every purchase order over any period — filter by vendor, buyer or status, total the spend, drill the line items. CSV + saved views.", Icon: ShoppingCart, live: true },
      { key: "vendor-list", title: "Vendors", blurb: "The whole supplier book — filter by state, terms or any field, group it, drill a vendor for contact/terms/account. CSV + saved views.", Icon: Truck, live: true },
      { key: "ticket-list", title: "Ticket History", blurb: "Every queue ticket ever — incoming, parts, missed calls — who cleared it and when.", Icon: ListChecks, live: true },
    ],
  },
  {
    label: "Analytics",
    reports: [
      { key: "revenue", title: "Revenue & Sales", blurb: "Revenue by period, department, zone or salesman.", Icon: DollarSign, live: true },
      { key: "margin", title: "Margin & Profitability", blurb: "Gross margin by department, salesperson, month or zone — revenue minus material, labor and other cost. Internal.", Icon: Percent, live: true },
      { key: "dispatch", title: "Dispatch Volume", blurb: "Job counts and mix by tech, department, zone, window or status.", Icon: Calendar, live: true },
      { key: "equipment-installs", title: "Equipment Installs", blurb: "Units installed by category, make, model, zone or month — over any period.", Icon: PackageSearch, live: true },
      { key: "lead-sources", title: "Lead Sources", blurb: "The whole book by where customers came from — totals, share, and new-this-year per source. CSV.", Icon: Megaphone, live: true },
      { key: "seasonal-demand", title: "Seasonal Demand", blurb: "Installs per model by season + a projection for next season — the order-planning view.", Icon: TrendingUp, live: true },
      { key: "agreements", title: "Agreements", blurb: "Build a report: filter the book by status, plan, value or dates; group it and total it.", Icon: ShieldCheck, live: true },
    ],
  },
  {
    label: "HR & Payroll",
    reports: [
      { key: "tech-payroll", title: "Tech Hours & Tickets", blurb: "Per-tech reg/OT hours, labor cost and invoice tickets over any pay period — expandable detail, CSV for ADP prep.", Icon: HardHat, live: true },
    ],
  },
  {
    label: "Lookups & Worklists",
    reports: [
      { key: "collections", title: "Collection Manager", blurb: "Every customer with an open balance, aged 30/60/90+, oldest first — phone on the row, CSV for the call sheet.", Icon: Banknote, live: true },
      { key: "membership-renewals", title: "Plan Renewals", blurb: "Plans expiring soon (renewal calls) or recently lapsed (win-back) — phone on the row, value at stake, link to the agreement. Derived from the dates, keeps itself current.", Icon: RefreshCw, live: true },
      { key: "tuneups-due", title: "Tune-ups Due", blurb: "Membership tune-ups Pegasus auto-created when an agreement was sold — book them before the season hits. One click drops a tune-up into the schedule queue.", Icon: Wrench, live: true },
      { key: "parts-day", title: "Parts for the Day", blurb: "A per-tech morning load sheet — the day's jobs grouped by tech, with the membership tune-ups flagged and the consumables to stock the truck.", Icon: Truck, live: true },
      { key: "serial-lookup", title: "Serial Lookup", blurb: "Find a unit by serial or model — whose it is, where, install date + warranty.", Icon: ScanLine, live: true },
      { key: "warranty-expiring", title: "Warranty Expiring", blurb: "Units whose warranty lapses soon, with phone/email — the proactive outreach list.", Icon: CalendarClock, live: true },
      { key: "refrigerant", title: "Refrigerant Log", blurb: "Every pound in and out — charged into new systems, recovered from old units (the EPA 608 record by month), recovery tanks and send-outs, stock counts. CSV.", Icon: Snowflake, live: true },
      { key: "stock-counts", title: "Stock Counts", blurb: "Every cycle count ever saved — shelf and trucks — plus the drift view: which parts keep coming up short. CSV.", Icon: ClipboardCheck, live: true },
    ],
  },
];

export const REPORTS: ReportCard[] = SECTIONS.flatMap((s) => s.reports);

/** Title for a `?report=` key, or null when unknown. */
export function reportTitle(key: string | null | undefined): string | null {
  if (!key) return null;
  return REPORTS.find((r) => r.key === key)?.title ?? null;
}
